/**
 * starNote学院相关工具函数
 * 参照 galaxy-admin 的实现保持一致
 */

/**
 * 根据语言获取多语言数据中的值
 * @param {Array} data - 多语言数据数组，格式: [{ locale: "zh_CN", value: "xxx" }, ...]
 * @param {String} locale - 语言代码: "zh_CN" | "zh_TW" | "en"
 * @param {Boolean} returnValue - 是否只返回值（字符串），默认 false 返回对象（用于 v-model 绑定）
 * @returns {Object|String} 对应语言的对象（用于 v-model 绑定）或值（用于显示）
 */
export function valueByLanguage(data, locale, returnValue = false) {
  if (!data || !Array.isArray(data)) {
    // 如果 data 不是数组，返回占位对象或空字符串
    return returnValue ? "" : { locale, value: "" };
  }
  let item = data.find((item) => item.locale === locale);
  // 如果不存在，创建一个新对象并添加到数组（确保响应式）
  if (!item) {
    item = { locale, value: "" };
    data.push(item);
  }
  // 根据 returnValue 参数决定返回对象还是值
  return returnValue ? (item.value || "") : item;
}

/**
 * 创建多语言数据格式
 * @param {String} zhCN - 简体中文值
 * @param {String} zhTW - 繁体中文值
 * @param {String} en - 英文值
 * @returns {Array} 多语言数据数组
 */
export function createLanguageData(zhCN = "", zhTW = "", en = "") {
  // 确保所有值都是字符串，避免 undefined 或 null
  return [
    { locale: "zh_CN", value: String(zhCN || "") },
    { locale: "zh_TW", value: String(zhTW || "") },
    { locale: "en", value: String(en || "") }
  ];
}

/**
 * 获取默认的多语言数据结构
 * @returns {Array} 默认的多语言数据数组
 */
export function getDefaultLanguageData() {
  return [
    { locale: "zh_CN", value: "" },
    { locale: "zh_TW", value: "" },
    { locale: "en", value: "" }
  ];
}

/**
 * 获取默认的文件多语言数据结构（值为数组）
 * @returns {Array} 默认的文件多语言数据数组
 */
export function getDefaultFileLanguageData() {
  return [
    { locale: "zh_CN", value: [] },
    { locale: "zh_TW", value: [] },
    { locale: "en", value: [] }
  ];
}

/**
 * 检查多语言文件数据是否完整（对齐 galaxy-admin 的 checkLangCompleteness）
 * @param {Object} data - 多语言文件数据对象，格式: { zh_CN: {...}, zh_TW: {...}, en: {...} }
 * @param {String} fieldValue - 字段名称，用于错误提示，默认为 "文件"
 * @returns {Object} 返回 { isValid: boolean, missingLangs: string[] }，调用者负责显示错误消息
 */
export function checkLangCompleteness(data, fieldValue = "文件") {
  try {
    if (!data) {
      return { isValid: false, missingLangs: ["zh_CN", "zh_TW", "en"], message: `找不到${fieldValue}数据` };
    }

    const languages = ["zh_CN", "zh_TW", "en"];
    const missingLangs = languages.filter(lang => {
      const item = data[lang];
      return !item || (!item.file && !item.url);
    });

    if (missingLangs.length > 0) {
      const langMap = { zh_CN: "简体", zh_TW: "繁体", en: "英文" };
      const names = missingLangs.map(lang => langMap[lang] || lang).join("、");
      return { isValid: false, missingLangs, message: `请完善${names}的${fieldValue}` };
    }

    return { isValid: true, missingLangs: [] };
  } catch (error) {
    console.error("checkLangCompleteness error:", error);
    return { isValid: false, missingLangs: ["zh_CN", "zh_TW", "en"], message: "检查失败" };
  }
}

